import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useIdioma } from '../i18n/idioma';
import { useGoogleMaps } from '../contexts/GoogleMapsContext';
import { MapPin, Search } from 'lucide-react';

interface AddressAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    onPlaceSelect?: (formattedAddress: string) => void;
    /** Fires true when address suggestions are working (API loaded + returning
     *  results or a clean "no matches"), false when the Places API is missing,
     *  not configured, or erroring. Lets a form relax a "must pick a suggestion"
     *  rule when suggestions genuinely can't be offered. */
    onSearchAvailabilityChange?: (available: boolean) => void;
    placeholder?: string;
    required?: boolean;
    /** Se lleva el cursor al montarse. Lo usa el cuadro que pide la dirección al
     *  convertir un prospecto, donde escribir la dirección es lo ÚNICO que hay
     *  que hacer y el cuadro se abre a media conversión. */
    autoFocus?: boolean;
    /** «He terminado» con la tecla Enter. Es para quien NO está dentro de un
     *  `<form>` y por tanto no recibe un submit —el cuadro de diálogo global—.
     *
     *  Quien no lo pasa no cambia en nada: sin esta prop no se toca la tecla, y
     *  los formularios que ya envían con Enter siguen enviando igual. */
    onEnter?: () => void;
    /** Solo lectura: se enseña la dirección pero no se escribe ni se buscan
     *  sugerencias. Lo usa el panel de un prospecto ya ganado, que está cerrado. */
    readOnly?: boolean;
    className?: string;
    iconSize?: number;
    iconClassName?: string;
}

interface Prediction {
    placeId: string;
    text: string;
    mainText: string;
    secondaryText: string;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
    value,
    onChange,
    onPlaceSelect,
    onSearchAvailabilityChange,
    placeholder,
    required = false,
    autoFocus = false,
    onEnter,
    readOnly = false,
    className = '',
    iconSize = 16,
    iconClassName = 'absolute left-3 top-3 text-acero-400',
}) => {
    const { t } = useIdioma();
    const { isLoaded } = useGoogleMaps();
    const inputRef = useRef<HTMLInputElement>(null);
    const [predictions, setPredictions] = useState<Prediction[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    // Last-status hint shown under the input when the API can't help.
    // Distinguishes "no matches" from "API not configured" so the contractor
    // (or you) knows whether to retype or to fix the Google project.
    const [statusHint, setStatusHint] = useState<string | null>(null);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    // Pixel coords for the portal dropdown — recomputed from the input's
    // bounding rect so the menu tracks the input through modal scroll, page
    // resize, and IME caret jumps.
    const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
                inputRef.current && !inputRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Track the input's screen position whenever the dropdown is visible so
    // the portal-rendered menu stays glued underneath through modal scroll,
    // window resize, and ancestor reflow.
    useLayoutEffect(() => {
        if (!showDropdown) return;
        const updatePosition = () => {
            if (!inputRef.current) return;
            const rect = inputRef.current.getBoundingClientRect();
            setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
        };
        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [showDropdown, predictions.length]);

    // Race any Promise against an 8s ceiling — Google Places sometimes hangs
    // (network jitter, blocked API, etc.) and a hung await leaves the spinner
    // spinning forever, masking the real failure.
    const withTimeout = <T,>(p: Promise<T>, ms: number, label: string): Promise<T> =>
        Promise.race([
            p,
            new Promise<T>((_, reject) =>
                setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms),
            ),
        ]);

    // Fetch predictions - tries new Places API first, falls back to legacy.
    // Sets statusHint so the contractor sees WHY there are no suggestions
    // (API not enabled / no matches / network error) instead of a silent fail.
    const fetchPredictions = async (input: string) => {
        if (!isLoaded || !input || input.length < 3) {
            setPredictions([]);
            setShowDropdown(false);
            setStatusHint(null);
            return;
        }

        setIsSearching(true);
        setStatusHint(null);
        let newApiTried = false;
        let newApiOk = false;

        try {
            // Plan A: new Places API. Reach for the namespace directly first
            // (works on already-loaded scripts) and only fall back to dynamic
            // importLibrary if the namespace isn't there yet.
            const placesNs: any =
                (google.maps.places as any)?.AutocompleteSuggestion
                    ? google.maps.places
                    : await (google.maps as any).importLibrary?.('places').catch(() => null);

            if (placesNs && placesNs.AutocompleteSuggestion) {
                newApiTried = true;
                try {
                    const request = {
                        input,
                        includedRegionCodes: ['us'],
                        includedPrimaryTypes: ['street_address', 'subpremise', 'route', 'locality', 'premise'],
                    };
                    const { suggestions } = await withTimeout<{ suggestions: any[] }>(
                        placesNs.AutocompleteSuggestion.fetchAutocompleteSuggestions(request),
                        8000,
                        'New Places API',
                    );
                    const results: Prediction[] = (suggestions || [])
                        .filter((s: any) => s.placePrediction)
                        .map((s: any) => ({
                            placeId: s.placePrediction.placeId || '',
                            text: s.placePrediction.text?.text || '',
                            mainText: s.placePrediction.mainText?.text || s.placePrediction.text?.text || '',
                            secondaryText: s.placePrediction.secondaryText?.text || '',
                        }));
                    newApiOk = true;
                    /* La disponibilidad se anuncia según HAYA resultados, no
                       según la API haya respondido. Antes se avisaba `true`
                       incluso con cero coincidencias, y entonces la validación
                       exigía "elige una de la lista" sobre una lista vacía: el
                       alta del cliente quedaba bloqueada sin salida. Pasa en
                       obra nueva, calles sin numerar y zonas rurales. */
                    onSearchAvailabilityChange?.(results.length > 0);
                    setPredictions(results);
                    setShowDropdown(results.length > 0);
                    if (results.length === 0) setStatusHint(t('direccion.sinResultados'));
                    return;
                } catch (newApiError) {
                    console.warn('[AddressAutocomplete] New Places API failed, trying legacy:', newApiError);
                }
            }

            // Plan B: legacy AutocompleteService. Wrap in a Promise so the
            // outer try/finally still sees the callback-driven outcome.
            if (google.maps.places && google.maps.places.AutocompleteService) {
                const service = new google.maps.places.AutocompleteService();
                await withTimeout(new Promise<void>((resolve) => {
                    service.getPlacePredictions(
                        { input, types: ['address'], componentRestrictions: { country: 'us' } },
                        (results, status) => {
                            if (status === google.maps.places.PlacesServiceStatus.OK && results) {
                                const preds: Prediction[] = results.map((r) => ({
                                    placeId: r.place_id,
                                    text: r.description,
                                    mainText: r.structured_formatting?.main_text || '',
                                    secondaryText: r.structured_formatting?.secondary_text || '',
                                }));
                                onSearchAvailabilityChange?.(true);
                                setPredictions(preds);
                                setShowDropdown(preds.length > 0);
                                if (preds.length === 0) setStatusHint(t('direccion.sinResultados'));
                            } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                                onSearchAvailabilityChange?.(true);
                                setPredictions([]);
                                setShowDropdown(false);
                                setStatusHint(t('direccion.sinResultados'));
                            } else {
                                console.warn('[AddressAutocomplete] Legacy Places API status:', status);
                                onSearchAvailabilityChange?.(false);
                                setPredictions([]);
                                setShowDropdown(false);
                                setStatusHint(t('direccion.buscadorNoDisponibleLargo', { estatus: status }));
                            }
                            resolve();
                        },
                    );
                }), 8000, 'Legacy Places API');
                return;
            }

            // Neither API path is available.
            console.warn('[AddressAutocomplete] No Places API available. Enable "Places API" or "Places API (New)" in Google Cloud Console for this project.');
            onSearchAvailabilityChange?.(false);
            setPredictions([]);
            setShowDropdown(false);
            setStatusHint(t('direccion.autocompletar.sinconfigurar'));
        } catch (error) {
            // warn, not error: this catch is effectively the 8s Places timeout
            // (client-network weather) — real misconfigurations return fast
            // explicit statuses via the branch above. UX already degrades below.
            console.warn('[AddressAutocomplete] Error fetching predictions:', error);
            onSearchAvailabilityChange?.(false);
            setPredictions([]);
            setShowDropdown(false);
            setStatusHint(t('direccion.autocompletar.error'));
        } finally {
            setIsSearching(false);
            // If the new API was the only one tried and it failed silently
            // (zero results, no exception) clear the misleading status.
            if (newApiTried && newApiOk === false) {
                // no-op; status already set above
            }
        }
    };

    // Handle selecting a prediction
    const handleSelectPrediction = async (prediction: Prediction) => {
        const address = prediction.text;
        onChange(address);
        onPlaceSelect?.(address);
        setShowDropdown(false);
        setPredictions([]);
    };

    // Handle input change with debounce
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        onChange(val);

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            fetchPredictions(val);
        }, 300);
    };

    return (
        <div className="relative">
            <MapPin className={iconClassName} size={iconSize} />
            <input
                ref={inputRef}
                type="text"
                required={required}
                autoFocus={autoFocus}
                readOnly={readOnly}
                className={className}
                placeholder={isLoaded ? (placeholder ?? t('direccion.placeholderBuscar')) : t('direccion.autocompletar.cargando')}
                value={value}
                onChange={handleInputChange}
                onFocus={() => {
                    if (predictions.length > 0) setShowDropdown(true);
                }}
                onKeyDown={(e) => {
                    if (e.key !== 'Enter' || !onEnter) return;
                    /* Con el desplegable ABIERTO, Enter se traga y no confirma
                       nada. Ahí la tecla se lee como «elijo esta sugerencia», y
                       cerrar el cuadro con lo escrito a medias —"1425 Elm"—
                       justo cuando la dirección buena está a la vista es el peor
                       de los dos posibles malentendidos: lo escrito acaba
                       impreso en la propuesta y metido en las rutas. */
                    e.preventDefault();
                    if (!showDropdown) onEnter();
                }}
                autoComplete="off"
            />
            {isSearching && (
                <div className="absolute right-3 top-3">
                    <div className="w-4 h-4 border-2 border-acero-200 border-t-brand-400 rounded-full animate-spin"></div>
                </div>
            )}
            {!isLoaded && (
                <div className="absolute right-3 top-3">
                    <div className="w-4 h-4 border-2 border-acero-200 border-t-brand-400 rounded-full animate-spin"></div>
                </div>
            )}
            {/* Visible diagnostic — only shown when there are no predictions
                to display. Clears as soon as predictions arrive. */}
            {statusHint && !showDropdown && (
                <p className="mt-1.5 text-xs text-aviso bg-aviso-bg border border-aviso/25 px-2.5 py-1.5 rounded-lg">
                    {statusHint}
                </p>
            )}
            {/* Predictions Dropdown — portaled to <body> so it escapes the
                modal's `overflow-y-auto` clipping context. Positioned with
                `position: fixed` and the input's live screen rect. */}
            {showDropdown && predictions.length > 0 && dropdownPos && createPortal(
                <div
                    ref={dropdownRef}
                    className="bg-white rounded-2xl shadow-s3 overflow-hidden"
                    style={{
                        position: 'fixed',
                        top: dropdownPos.top,
                        left: dropdownPos.left,
                        width: dropdownPos.width,
                        zIndex: 10000,
                    }}
                >
                    {predictions.map((prediction, index) => (
                        <button
                            key={prediction.placeId || index}
                            type="button"
                            className="w-full px-4 py-3 text-left hover:bg-acero-50 transition-colors duration-150 flex items-start gap-3 border-b border-acero-100 last:border-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400"
                            onClick={() => handleSelectPrediction(prediction)}
                        >
                            <MapPin size={14} className="text-acero-400 mt-0.5 flex-shrink-0" />
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-navy-950 truncate">
                                    {prediction.mainText}
                                </div>
                                {prediction.secondaryText && (
                                    <div className="text-xs text-acero-500 truncate">
                                        {prediction.secondaryText}
                                    </div>
                                )}
                            </div>
                        </button>
                    ))}
                    <div className="px-4 py-2 bg-acero-50 border-t border-acero-100 flex items-center justify-end">
                        <img
                            src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png"
                            alt="Powered by Google"
                            className="h-4"
                        />
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default AddressAutocomplete;
